/**
 * @author Mohammad Sarfraj
 * @date 26-Nov-2024
 * @module_name Checkin.jsx
 */
import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row, Table, Button, Form, Modal } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import SparkApi from "../../api/SparkApi";
import Select from "react-select";
import { DatatableWrapper, TableBody, TableHeader } from "react-bs-datatable";
import { toast, ToastContainer } from "react-toastify";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import imageCompression from "browser-image-compression";

const Checkin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [factorydata, setFactory] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [rackOptions, setRackOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [searchText, setSearchText] = useState();
  const [isScannerVisible, setScannerVisible] = useState(false); // Control the scanner visibility
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const [productInventory, setProductInventory] = useState();

  useEffect(() => {
    if (productInventory?.product_barcode) {
      fetchProductDetails();
    }
  }, [productInventory?.product_barcode]);

  /*---------  Start barcode ----------------*/
  const [barcodeRead, setBarcodeRead] = useState();
  
  const barcodeRef = useRef({
    timing: 0,
    data: "",
  });

  useEffect(() => {
    const barcodeReaded = () => {
      if (
        barcodeRef.current.data.length > 1 &&
        barcodeRef.current.data.includes("PRO")
      ) {
        setBarcodeRead(barcodeRef.current.data.split("E")[0]);
      }
      barcodeRef.current = { timing: 0, data: "" };
    };

    let timeout;

    const handleKeypress = (e) => {
      const currentTime = e.timeStamp;
      const isWithinInterval = currentTime - barcodeRef.current.timing < 200;

      if (barcodeRef.current.data.length === 0 || isWithinInterval) {
        clearTimeout(timeout);
        barcodeRef.current.data += e.key;
        barcodeRef.current.timing = currentTime;
        timeout = setTimeout(barcodeReaded, 300);
      } else {
        clearTimeout(timeout);
        barcodeRef.current = {
          timing: currentTime,
          data: e.key,
        };
      }
    };

    window.addEventListener("keypress", handleKeypress);

    return () => {
      window.removeEventListener("keypress", handleKeypress);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (barcodeRead) {
      fetchProductDetails();
    }
  }, [barcodeRead]);

  const fetchProductDetails = async () => {
    try {
      const productResult = await SparkApi.fetchScannedProductDetails(
        barcodeRead || productInventory?.product_barcode
      );

      if (productResult?.records[0]?.result?.invent[0]?.date === null) {
        productResult?.records[0]?.result?.invent?.pop();
      }

      const newRow = {
        date: getCurrentDate(),
        factory_id: null,
        warehouse_id: null,
        rack_id: null,
        type: "check-in",
        remark: "",
        quantity: 0,
        supplier: "",
        scannedLength: productResult?.records[0]?.result?.invent?.length,
        isScan: false,
      };

      if (productResult?.records.length > 0) {
          productResult?.records[0]?.result?.invent?.unshift(newRow);

        setProductInventory((prev) => ({
          ...prev,
          product_id: productResult?.records[0]?.result?.product_id,
          product_name: productResult?.records[0]?.result?.product_name,
          invent: productResult?.records[0]?.result?.invent.map(
            (object, index) => ({
              ...object,
              isScan: object?.factory_id === null ? false : true,
              index: index,
              type: object?.type || object?.type || "check-in",
            })
          ),
        }));

        setProductOptions([
          {
            value: productResult?.records[0]?.result?.product_id,
            label: productResult?.records[0]?.result?.product_name,
          },
        ]);

        setBarcodeRead(null);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };
  /*---------   End barcode   ------------*/

  useEffect(() => {
    fetchAllFactory();
    fetchAllWarehouses();
    fetchAllRacks();

    if (location.state) {
      if(location.state?.productBarcode){
        setProductInventory((prev) => ({
          ...prev,
          product_barcode : location?.state?.productBarcode,
        }));
      }
    }
  }, [location?.state]);

  const fetchAllFactory = async () => {
    try {
      const result = await SparkApi.fetchActiveFactory();
      if (result && Array.isArray(result)) {
        setFactory(result.map((sub) => ({ value: sub.id, label: sub.name })));
      } else if (result) {
        setFactory([{ value: result.id, label: result.name }]);
      }
    } catch (err) {
      console.log("Error", err);
    }
  };

  const fetchAllWarehouses = async () => {
    try {
      const warehouses = await SparkApi.fetchWarehouse();
      setWarehouseOptions(
        warehouses.map((sub) => ({
          value: sub.id,
          label: sub.name,
          factory_id: sub.factory_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchAllRacks = async () => {
    try {
      const racks = await SparkApi.fetchActiveRacks();
      setRackOptions(
        racks.data.map((rackRec) => ({
          value: rackRec.id,
          label: rackRec.name,
          warehouse_id: rackRec.warehouse_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching racks:", error);
    }
  };

  const fetchAllProducts = async () => {
    try {
      if (searchText?.trim()) {
        if (searchText.length > 0 && searchText !== "") {
          const products = await SparkApi.getProductsBySearch(searchText);
          if (products.success) {
            setProductOptions(
              products.records.map((ProRec) => ({
                value: ProRec?.id,
                label: ProRec?.name,
                quantity: ProRec?.quantity,
                barcode: ProRec?.barcode,
              }))
            );
          }
        }
      } else {
        setProductOptions([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleAddRow = async () => {
    const newRow = {
      isScan: false,
      index: productInventory.invent.length-1,
      date: getCurrentDate(),
      factory_id: null,
      warehouse_id: null,
      rack_id: null,
      type: "check-in",
      remark: "",
      quantity: 0,
      supplier: "",
    };

    productInventory?.invent?.unshift(newRow);

    setProductInventory((prevInventory) => ({
        ...prevInventory ,
        invent: productInventory?.invent?.map((object, index) => ({
          ...object,
          // isScan: object?.factory_id === null ? false : true,
          isScan: object?.isScan, //=== null ? false : true,
          index: index,
          type: object?.type || object?.type || "check-in",
      }))
    }));

    return newRow;
  };

  const handleDeleteRow = (rowIndex) => {
    setProductInventory((prev) => {
      const newRecord = prev.invent?.filter((item ,index) => item.isScan === false);

      const emptyRows = newRecord?.filter(item => 
        !item.factory_id && !item.warehouse_id && !item.rack_id && 
        !item.remark && item.quantity === 0 && !item.supplier
      );


      if(newRecord?.length === 1) return prev;
  
      if (newRecord?.length === 1 && emptyRows.length === 0 && emptyRows[0] === prev.invent[rowIndex]) return prev;

      if(prev?.invent?.length === 1){
        return prev;
      }
  
      const updatedInventory = prev.invent.filter((_, index) => index !== rowIndex);
      
      return { 
        ...prev, 
        invent: updatedInventory.map((item, index) => ({ ...item, index })) 
      };
    });
  };
  const handleInputChange = (e, rowIndex) => {
    if (e.target.name === "quantity") {

      setProductInventory((prev) => {
        const updatedInvent = prev.invent.map((item) => {
          if (item.index === rowIndex) {
            return {
              ...item,
              [e.target.name]: parseInt(e.target.value),
              // isScan: location?.state ? true : false,
              isScan: false,
            };
          }
          return item;
        });
        return { ...prev, invent: updatedInvent };
      });
    } else {
      setProductInventory((prev) => {
        const updatedInvent = prev.invent.map((item) => {
          if (item.index === rowIndex) {
            return {
              ...item,
              [e.target.name]: e.target.value,
              // isScan: location?.state ? true : false,
              isScan: false,
            };
          }
          return item;
        });
        return {
          ...prev,
          invent: updatedInvent,
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    try {
      // Only validate new rows (isScan: false). Historical rows (isScan: true) are
      // already saved and may have null fields — validating them causes false errors.
      const newRows = productInventory?.invent?.filter((row) => row.isScan === false) || [];

      for (let i = 0; i < newRows.length; i++) {
        if (
          newRows[i]?.quantity <
          newRows[i]?.checkout_quantit
        ) {
          return toast.error("Check-in quantity can not less than check-out quantity");
        }
        if (
          !newRows[i]?.factory_id ||
          !newRows[i]?.warehouse_id ||
          !newRows[i]?.rack_id ||
          !newRows[i]?.quantity
        ) {
          return toast.error("Please fill all required fields: Factory, Warehouse, Rack and Quantity");
        }
      }

      if (newRows.length === 0) {
        return toast.error("Please add at least one check-in row");
      }

      e.preventDefault();
      let result;
      if (location.state?.product_id) {
        result = await SparkApi.updateProductinventory(
          productInventory.product_id,
          productInventory
        );
      } else {
        result = await SparkApi.createProductInventory(productInventory);
      }

      if (result?.success) {
        navigate("/productinventory");
        // navigate("/products");
      } else {
        console.error("API call failed:", result?.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error submitting product:", error);
    }
  };

  const Header = [
    {
      title: "Date",
      prop: "date",
      cell: (row, index) => (
        <Form.Control
          disabled={row?.isScan ? true : false}
          type="date"
          name="date"
          placeholderText="dd-mm-yyyy"
          value={row?.date?.split("T")[0]}
          onChange={(e) => handleInputChange(e, row?.index)}
        />
      ),
    },
    {
      title: "Factory",
      prop: "",
      cell: (row) => (
        <Select 
          name="factory_id"
          options={factorydata || []}
          value={productInventory?.invent[row?.index]?.factory_id && factorydata?.find((option) => option?.value === row?.factory_id)}
          onChange={(selected) => {
            setProductInventory((prevInventory) => ({
              ...prevInventory,
              invent: prevInventory.invent.map((item, index) =>
                index === row?.index
                  ? {
                    ...item,
                    factory_id: selected?.value,
                    warehouse_id: null, 
                    rack_id: null,
                    isScan: false,
                  }
                  : item
              ),
            }));
          }}
          isClearable={true}
          isDisabled={row?.isScan ? true : false}
          required
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            control: (base) => ({
              ...base,
              borderLeftColor: "red",
              borderWidth: "3px",
            }),
          }}
          menuPosition="fixed"
        />
      ),
    },
    {
      title: "WareHouse",
      prop: "",
      cell: (row) => {
        const warehouseOptionsFiltered = warehouseOptions.filter(
          (warehouseObj) => warehouseObj?.factory_id === row?.factory_id
        );

        const selectedWarehouse = warehouseOptionsFiltered.find(
          (warehouseObj) => warehouseObj?.value === row?.warehouse_id
        );

        return (
          <Select
            isDisabled={row?.isScan ? true : false}
            name="warehouse_id"
            options={warehouseOptionsFiltered}
            value={selectedWarehouse || null} 
            onChange={(selected) => {
              setProductInventory((prevInventory) => ({
                ...prevInventory,
                invent: prevInventory.invent.map((item, index) =>
                  index === row?.index
                    ? {
                      ...item,
                      warehouse_id: selected?.value || null,
                      rack_id: null, 
                      isScan: false,
                    }
                    : item
                ),
              }));
            }}
            isClearable={true}
            required
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              control: (base) => ({
                ...base,
                borderLeftColor: "red",
                borderWidth: "3px",
              }),
            }}
            menuPosition="fixed"
          />
        );
      },
    },
    {
      title: "Rack",
      prop: "",
      cell: (row) => {
        const rackOptionsFiltered = rackOptions.filter(
          (record) => record?.warehouse_id === row?.warehouse_id
        );

        const selectedRack = rackOptionsFiltered.find(
          (option) => option?.value === row?.rack_id
        );

        return (
          <Select
            isDisabled={row?.isScan ? true : false}
            name="rack_id"
            options={rackOptionsFiltered}
            value={selectedRack || null} // Fallback to null if no valid rack is found
            onChange={(selected) => {
              setProductInventory((prevInventory) => ({
                ...prevInventory,
                invent: prevInventory.invent.map((item, index) =>
                  index === row?.index
                    ? {
                      ...item,
                      rack_id: selected?.value || null,
                      isScan: false,
                    }
                    : item
                ),
              }));
            }}
            isClearable={true}
            required
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              control: (base) => ({
                ...base,
                borderLeftColor: "red",
                borderWidth: "3px",
              }),
            }}
            menuPosition="fixed"
          />
        );
      },
    },
    {
      title: "Quantity",
      prop: "",
      cell: (row) => (
        <Form.Control
          disabled={row?.isScan ? true : false}
          type="numeric"
          name="quantity"
          placeholder="Quantity"
          value={row.quantity || ""}
          onChange={(e) => {
            if (!e.target.value === "" || !isNaN(e.target.value)) {
              handleInputChange(e, row?.index);
            }
          }}

          required
          min={0}
        />
      ),
    },
    {
      title: "Type",
      prop: "",
      cell: (row) => (
        <Form.Select
          required
          name="type"
          disabled //={row?.isScan ? location?.state ? false : true : false}
          value={row?.type || ""}
          onChange={(e) => {
            handleInputChange(e, row?.index);
          }}
        >
          <option value="" disabled>
            -- Select Type --
          </option>
          <option value="check-in">Check-in</option>
          <option value="check-out">Check-out</option>
        </Form.Select>
      ),
    },
    {
      title: "Remark",
      prop: "",
      cell: (row) => (
        <Form.Control
          disabled={row?.isScan ? true : false}
          type="text"
          placeholder="Enter remark"
          name="remark"
          value={row?.remark}
          onChange={(e) => handleInputChange(e, row?.index)}
        />
      ),
    },
    {
      title: "Supplier",
      prop: "",
      cell: (row) => (
        <Form.Control
          disabled={row?.isScan ? true : false}
          type="text"
          name="supplier"
          placeholder="Supplier name"
          value={row?.supplier}
          onChange={(e) => handleInputChange(e, row?.index)}
        />
      ),
    },
    {
      title: "Action",
      prop: "action",
      cell: (row) => (
        <div className="action-column">
          {location.state?.success !== true && (
            <Button
              disabled={row?.isScan}
              onClick={() => handleAddRow()}
              variant="success"
              className="btn-sm mx-1"
            >
              <i className="fa fa-plus" />
            </Button>
          )}
          <Button
            disabled={row?.isScan ? true : false}
            className="btn-sm"
            variant="danger"
            onClick={() =>
              handleDeleteRow(row.index, row?.isScan, row?.scannedLength)
            }
          >
            <i className="fa fa-trash" />
          </Button>
        </div>
      ),
    },
  ];

  const handleScanButton = () => {
    setScannerVisible(true);
  };

  const handleImageFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const options = { maxSizeMB: 0.2, maxWidthOrHeight: 1280, initialQuality: 0.6, useWebWorker: true };
    const compressed = await Promise.all(
      files.map(async (f) => {
        const blob = await imageCompression(f, options);
        return new File([blob], f.name, { type: "image/jpeg" });
      })
    );
    setSelectedImages(compressed);
  };

  const handleImageUpload = async () => {
    if (!selectedImages.length || !productInventory?.product_id) return;
    setIsUploadingImages(true);
    try {
      for (const file of selectedImages) {
        const formData = new FormData();
        formData.append("image", file);
        await SparkApi.createFile(productInventory.product_id, formData);
      }
      toast.success(`${selectedImages.length} image${selectedImages.length > 1 ? "s" : ""} uploaded successfully`);
      setSelectedImages([]);
      setShowImageModal(false);
    } catch (err) {
      toast.error("Failed to upload images");
    }
    setIsUploadingImages(false);
  };

  return (
    <Container>
        <div style={{ textAlign: "center" }}>
          {isScannerVisible && (
            <>
              <BarcodeScannerComponent
                width={250}
                height={250}
                facingMode="environment"
                onUpdate={(err, result) => {
                  if (result){
                    setBarcodeRead(result.text);
                    setScannerVisible(false);
                  } 
                  else {
                    setBarcodeRead("Not Found");
                    // setScannerVisible(false);
                  }
                }}
              />
              {/* <button onClick={handleStopScan}>Stop Scanning</button> */}
            </>
          )}
          {/* <p>Scanned Data: {barcodeRead}</p> */}
        </div>

      <ToastContainer
        position="top-center"
        autoClose={1500}
        closeOnClick
        theme="light"
      />

      <Row className="mt-3">
        <Col lg={3}>
          <Link className="nav-link" to="/products">
            Home <i className="fa-solid fa-chevron-right"></i>{location?.state?.productBarcode ? ' Product ' : ` Products `}<i className="fa-solid fa-chevron-right"></i>
            <span style={{ color: "#23468c" }}> Check In</span>
          </Link>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col lg={4} md={6} xs={12}>
          {location?.state?.product_id && location?.state?.product_name ? (
            <Form.Select disabled name="product_id">
              <option selected>{productInventory?.product_name}</option>
            </Form.Select>
          ) : (
            <Select
              id="product_id"
              name="product_id"
              options={productOptions}
              value={productOptions?.find((option) =>
                option?.value === productInventory?.product_id
                  ? productInventory?.product_id
                  : location?.state?.product_id
              )}
              onInputChange={(inputValue) => {
                if (inputValue?.length < (searchText?.length || 0)) {
                  setSearchText(inputValue);
                  return;
                }
                setSearchText(inputValue);
                setBarcodeRead();
                fetchAllProducts();
              }}
              onChange={(select) =>
                setProductInventory((prev) => ({
                  ...prev,
                  product_id: select?.value,
                  product_barcode: select?.barcode,
                }))
              }
              isClearable={true}
              isSearchable={true}
              placeholder="--Enter Product Name--"
              required
            />
          )}
        </Col>
        <Col lg={4} md={1}></Col>
        <Col lg={4} md={5} className="text-start text-sm-end checkin-checkout-drop-dwon-button-margion-top">
          <Button
            // onClick={() => navigate("/productinventory")}
            onClick={() => navigate("/products")}
            variant="danger"
            className="btn-sm mx-2"
          >
            Cancel
          </Button>
          <Button 
            variant="success"
            className="btn-sm"
            onClick={handleSubmit}
            disabled={!productInventory?.product_id}
          >
            Save
          </Button>
          <Button className="btn-sm scanButton" onClick={handleScanButton}>Scan Barcode</Button>
          <Button
            className="btn-sm"
            variant="outline-secondary"
            disabled={!productInventory?.product_id}
            onClick={() => setShowImageModal(true)}
          >
            Add Images
          </Button>
        </Col>
      </Row>

      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={() => { setShowImageModal(false); setSelectedImages([]); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Add Images — {productInventory?.product_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Images <small className="text-muted">(one or more)</small></Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageFileChange}
            />
            {selectedImages.length > 0 && (
              <div className="mt-2 text-muted" style={{ fontSize: "0.85em" }}>
                {selectedImages.length} image{selectedImages.length > 1 ? "s" : ""} selected
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => { setShowImageModal(false); setSelectedImages([]); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleImageUpload}
            disabled={!selectedImages.length || isUploadingImages}
          >
            {isUploadingImages ? "Uploading..." : "Upload"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Row className="g-0 mt-4">
        <Col lg={12}>
          <DatatableWrapper
            headers={Header}
            body={productInventory?.invent || []}
            paginationOptionsProps={{ initialState: { rows: 10, page: 1 } }}
          >
            <Table responsive className="checkin-table">
              <TableHeader />
              {productInventory && <TableBody />}
            </Table>
          </DatatableWrapper>
        </Col>
      </Row>
    </Container>
  );
};
export default Checkin;
